import React from 'react';
import { APITypes } from '../../../primary-record-types';
import usePayments from '../../../hooks/api/usePayments';
import Alert from '../../v2BaseComponents/Alert';
import { getIntl } from '../../../utils/intl';
import { getCommaList } from '../../../utils/getCommaList';
import { entitlements } from '../../../utils/sharedMessages';
import { usePromptButton } from '../PromptButton';
import getDateString from '../../../utils/getDateString';
import { ContentStack } from '../../v2BaseComponents/Stack';
import { PromptActions } from '../../v2BaseComponents/Prompt';
import Text from '../../v2BaseComponents/Text';

export type ActiveRegistrationCodeAlertOwnProps = {
    status:
        | APITypes.paymentService.ActiveDiscountRegistrationCodeStatus
        | APITypes.paymentService.ActiveEntitlementRegistrationCodeStatus;
    disableRemoveCode?: boolean;
    showMoreDetails?: boolean;
};

export type ActiveRegistrationCodeAlertProps = ActiveRegistrationCodeAlertOwnProps;

const intl = getIntl('ActiveRegistrationCodeAlert', {
    alertHeader: 'Coupon Code',
    entitlementSubHeader: 'Free {entitlement} - {code}',
    entitlementWithMonthsSubHeader: 'Free {entitlement} for {months} months - {code}',
    entitlementWithExpirationSubHeader: 'Free {entitlement} until {expiration} - {code}',
    percentSubHeader: '{value}% off - {code}',
    alertSubText: 'More details in the My Account Subscription Section',
    removeCode: 'Remove Code',
    areYouSure: 'Are you sure you want to remove this coupon code?',
    yes: 'Yes, Remove',
    cancel: 'Cancel',
});

const getAlertSubHeader = (
    status:
        | APITypes.paymentService.ActiveDiscountRegistrationCodeStatus
        | APITypes.paymentService.ActiveEntitlementRegistrationCodeStatus,
) => {
    if (status.type === 'discount') {
        return intl('percentSubHeader', {
            value: status.discountRate,
            code: status.code,
        });
    } else if (status.durationInMonths) {
        return intl('entitlementWithMonthsSubHeader', {
            entitlement: getCommaList(status.entitlements.map((e) => entitlements(e))),
            months: status.durationInMonths,
            code: status.code,
        });
    } else if (status.expirationDateTime) {
        return intl('entitlementWithExpirationSubHeader', {
            entitlement: getCommaList(status.entitlements.map((e) => entitlements(e))),
            expiration: getDateString(status.expirationDateTime, 'date'),
            code: status.code,
        });
    }

    return intl('entitlementSubHeader', {
        entitlement: getCommaList(status.entitlements.map((e) => entitlements(e))),
        code: status.code,
    });
};

const ActiveRegistrationCodeAlert: React.FC<ActiveRegistrationCodeAlertProps> = ({
    status,
    disableRemoveCode,
    showMoreDetails = true,
}) => {
    const { isDeletingRegistrationCode, deleteRegistrationCode } = usePayments({});
    const { onPress, Prompt } = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Text variant="body-1">{intl('areYouSure')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('yes'),
                            onPress: () => {
                                onClose();
                                deleteRegistrationCode();
                            },
                            variant: 'alert',
                            isDisabled: isDeletingRegistrationCode,
                            testID: 'remove-code-confirmation-button',
                        },
                        {
                            title: intl('cancel'),
                            onPress: onClose,
                            variant: 'secondary',
                            isDisabled: isDeletingRegistrationCode,
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle: intl('removeCode'),
    });
    return (
        <>
            <Alert
                title={intl('alertHeader')}
                message={
                    <>
                        <Text variant="subtitle-2">
                            {`${getAlertSubHeader(status)}${showMoreDetails ? '\n' + intl('alertSubText') : ''}`}
                        </Text>
                    </>
                }
                type="success"
                actionItems={
                    disableRemoveCode
                        ? []
                        : [
                              {
                                  title: intl('removeCode'),
                                  onPress,
                                  isLoadingDisabled: isDeletingRegistrationCode,
                                  testID: 'remove-code-button',
                              },
                          ]
                }
                justifyActionItems="center"
            />

            {Prompt}
        </>
    );
};

export default React.memo(ActiveRegistrationCodeAlert);
